import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Timer, LogOut } from "lucide-react";
import { theme } from "../theme";
import { BlueprintBackdrop } from "../components/BlueprintBackdrop";
import { SIGN_IN as C, signInSecondaryButton, signInSheet } from "../lib/signInPalette";

// A page can only close a tab it opened itself; every other tab stays put and
// window.close() is a no-op, so fall back to telling the person how to close it.
const closeShortcut = /Mac|iPhone|iPad/i.test(navigator.userAgent) ? "⌘W" : "Ctrl + W";

export function SignedOutPage() {
  const [params] = useSearchParams();
  const expired  = params.get("reason") === "inactivity";
  const [closeBlocked, setCloseBlocked] = useState(false);

  const closeTab = () => {
    window.close();
    // window.close() throws nothing when it is refused — the tab is simply still here.
    window.setTimeout(() => {
      if (!window.closed) setCloseBlocked(true);
    }, 150);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.font,
        padding: 16,
        position: "relative",
      }}
    >
      <BlueprintBackdrop surface="login" />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 1, width: "100%", maxWidth: 420 }}>
        <img
          src="/abacas-logo.png"
          alt="Abacas Security"
          style={{ width: 88, height: 88, objectFit: "contain", marginBottom: 24, filter: "drop-shadow(0 0 24px rgba(255,0,170,0.6))" }}
        />

        <div style={{ ...signInSheet, padding: 32, textAlign: "center" }}>
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
            {expired ? <Timer size={40} color={C.accent} /> : <LogOut size={40} color={C.accent} />}
          </div>

          <div style={{ fontSize: 18, fontWeight: 700, color: C.heading, marginBottom: 8 }}>
            {expired ? "Session expired" : "You have been signed out"}
          </div>

          <div style={{ fontSize: 13, color: C.subtle, marginBottom: 28, lineHeight: 1.6 }}>
            {expired
              ? "You were automatically signed out after 10 minutes of inactivity."
              : "You have been signed out of the Abacas Security Compliance Platform."}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <a
              href="/"
              style={{
                display: "block",
                textDecoration: "none",
                textAlign: "center",
                background: C.accent,
                color: C.btnText,
                border: "none",
                borderRadius: theme.radius.sm,
                padding: "10px 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sign in again
            </a>

            {closeBlocked ? (
              <div
                role="status"
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: theme.radius.sm,
                  padding: "10px 16px",
                  fontSize: 12,
                  color: C.subtle,
                  lineHeight: 1.6,
                }}
              >
                Your browser only lets a page close a tab it opened itself. Press{" "}
                <span style={{ fontWeight: 600, color: C.heading }}>{closeShortcut}</span> to close
                this one.
              </div>
            ) : (
              <button onClick={closeTab} style={signInSecondaryButton}>
                Close this tab
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
